<script setup lang="ts" generic="TRow = unknown">
import type {
  BaseGridPlugin,
  ColumnConfig,
  ColumnInferenceMode,
  DataGridElement,
  FitMode,
  GridConfig,
} from '@toolbox-web/grid';
import { DataGridElement as GridElement } from '@toolbox-web/grid';
import type {
  BaselinesCapturedDetail,
  BeforeEditCloseDetail,
  CellActivateDetail,
  CellCancelDetail,
  CellChangeDetail,
  CellClickDetail,
  CellCommitDetail,
  ChangedRowsResetDetail,
  ClipboardConfig,
  ColumnMoveDetail,
  ColumnResizeDetail,
  ColumnResizeResetDetail,
  ColumnVirtualizationConfig,
  ColumnVisibilityDetail,
  ContextMenuConfig,
  ContextMenuOpenDetail,
  CopyDetail,
  DataChangeDetail,
  DataGridEventMap,
  DetailExpandDetail,
  DirtyChangeDetail,
  EditCloseDetail,
  EditOpenDetail,
  ExportCompleteDetail,
  ExportConfig,
  FilterChangeDetail,
  FilterConfig,
  GridColumnState,
  GroupCollapseDetail,
  GroupExpandDetail,
  GroupingColumnsConfig,
  GroupingRowsConfig,
  GroupToggleDetail,
  MasterDetailConfig,
  MultiSortConfig,
  PasteDetail,
  PasteRejectedDetail,
  PinnedRowsConfig,
  PivotConfig,
  PrintCompleteDetail,
  PrintConfig,
  PrintStartDetail,
  RenderDetail,
  ReorderConfig,
  ResponsiveChangeDetail,
  ResponsivePluginConfig,
  RowClickDetail,
  RowCommitDetail,
  RowDragDropConfig,
  RowDragEndDetail,
  RowDragStartDetail,
  RowDropDetail,
  RowMoveDetail,
  RowTransferDetail,
  SelectionChangeDetail,
  SelectionConfig,
  ServerSideConfig,
  SortChangeDetail,
  StickyRowsConfig,
  TbwScrollDetail,
  TooltipConfig,
  TreeConfig,
  TreeExpandDetail,
  TreeLoadEndDetail,
  TreeLoadErrorDetail,
  TreeLoadStartDetail,
  UndoRedoConfig,
  UndoRedoDetail,
  VisibilityConfig,
} from '@toolbox-web/grid/all';
import { createPluginsFromFeatures } from '@toolbox-web/grid/features/registry';
import { computed, nextTick, onBeforeUnmount, onMounted, provide, ref, watch, type PropType } from 'vue';
import { applyColumnDefaults, normalizeColumns, type ColumnShorthand } from './column-shorthand';
import { getFeaturePropKeys } from './feature-prop-keys';
import { useGridIcons } from './grid-icon-registry';
import { useGridTypeDefaults } from './grid-type-registry';
import { setTeleportManager } from './teleport-bridge';
import { TeleportManager, type TeleportManagerHandle } from './teleport-manager';
import { GRID_ELEMENT_KEY } from './use-grid';
import { notifyPostMount } from './post-mount-refresh-hooks';
import { computeRowDiff } from './row-diff';
import { GridAdapter } from './vue-grid-adapter';

// Track if adapter is registered
let adapterRegistered = false;
let globalAdapter: GridAdapter | null = null;

/**
 * Ensure the Vue adapter is registered globally.
 */
function ensureAdapterRegistered(): GridAdapter {
  if (!adapterRegistered) {
    globalAdapter = new GridAdapter();
    GridElement.registerAdapter(globalAdapter);
    adapterRegistered = true;
  }
  return globalAdapter as GridAdapter;
}

// Register adapter at module load
ensureAdapterRegistered();

// TbwGrid renders a fragment root (<TeleportManager> + <tbw-grid>). Disable
// Vue's automatic attribute fallthrough — we forward `class`, `style` and any
// other unknown attribute onto the inner `<tbw-grid>` custom element via
// `v-bind="$attrs"` in the template instead. Without this, Vue logs an
// "Extraneous non-props attributes" warning whenever consumers add `class=`
// or any custom attribute to the component.
defineOptions({ inheritAttrs: false });

/**
 * Props for TbwGrid component
 */
const props = defineProps({
  /** Row data to display */
  rows: {
    type: Array as PropType<TRow[]>,
    default: () => [],
  },
  /**
   * Column definitions. Accepts either full ColumnConfig objects or shorthand
   * strings such as `'name'` or `'salary:number'`. Shorthands auto-generate
   * human-readable headers from the field name.
   *
   * @example
   * ```vue
   * <TbwGrid :columns="['id:number', 'name', { field: 'status', editable: true }]" />
   * ```
   */
  columns: {
    type: Array as PropType<ColumnShorthand<TRow>[]>,
    default: undefined,
  },
  /**
   * Default column properties applied to every column in `columns`.
   * Individual column properties override these defaults.
   *
   * @example
   * ```vue
   * <TbwGrid
   *   :column-defaults="{ sortable: true, resizable: true }"
   *   :columns="[{ field: 'id', sortable: false }, { field: 'name' }]"
   * />
   * ```
   */
  columnDefaults: {
    type: Object as PropType<Partial<ColumnConfig<TRow>>>,
    default: undefined,
  },
  /** Full grid configuration */
  gridConfig: {
    type: Object as PropType<GridConfig<TRow>>,
    default: undefined,
  },
  /**
   * Manually instantiated plugins (escape hatch for advanced configuration).
   * When provided, feature props are ignored — only plugins from this list
   * plus any declared in `gridConfig.plugins` are used.
   *
   * @example
   * ```ts
   * import { SelectionPlugin } from '@toolbox-web/grid/plugins/selection';
   * const plugins = [new SelectionPlugin({ mode: 'range', checkbox: true })];
   * // <TbwGrid :rows="rows" :plugins="plugins" />
   * ```
   */
  plugins: {
    type: Array as PropType<BaseGridPlugin[]>,
    default: undefined,
  },
  /** Fit mode shorthand */
  fitMode: {
    type: String as PropType<FitMode>,
    default: undefined,
  },
  /**
   * How automatic column inference combines with explicitly provided columns.
   * - `'auto'` (default): infer only when no columns are provided.
   * - `'merge'`: always infer from data, then overlay provided columns by `field`.
   */
  columnInference: {
    type: String as PropType<ColumnInferenceMode>,
    default: undefined,
  },

  // ═══════════════════════════════════════════════════════════════════
  // GRID-WIDE TOGGLES
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Grid-wide sorting toggle.
   * When false, disables sorting for all columns regardless of their individual `sortable` setting.
   * @default true
   */
  sortable: {
    type: Boolean as PropType<boolean>,
    default: undefined,
  },
  /**
   * Grid-wide filtering toggle.
   * When false, disables filtering for all columns regardless of their individual `filterable` setting.
   * Requires the FilteringPlugin to be loaded (via `filtering` prop or feature import).
   * @default true
   */
  filterable: {
    type: Boolean as PropType<boolean>,
    default: undefined,
  },
  /**
   * Grid-wide selection toggle.
   * When false, disables selection for all rows/cells.
   * Requires the SelectionPlugin to be loaded (via `selection` prop or feature import).
   * @default true
   */
  selectable: {
    type: Boolean as PropType<boolean>,
    default: undefined,
  },
  /**
   * Show a loading overlay on the grid.
   * Use this during initial data fetch or refresh operations.
   * @default false
   */
  loading: {
    type: Boolean as PropType<boolean>,
    default: undefined,
  },
  /**
   * Custom CSS styles to inject into the grid via `document.adoptedStyleSheets`.
   */
  customStyles: {
    type: String as PropType<string>,
    default: undefined,
  },

  // ═══════════════════════════════════════════════════════════════════
  // FEATURE PROPS - Declarative plugin configuration
  // ═══════════════════════════════════════════════════════════════════

  /** Enable cell/row/range selection */
  selection: {
    type: [String, Object] as PropType<'cell' | 'row' | 'range' | SelectionConfig<TRow>>,
    default: undefined,
  },
  /** Enable inline cell editing */
  editing: {
    type: [Boolean, String] as PropType<boolean | 'click' | 'dblclick' | 'manual'>,
    default: undefined,
  },
  /** Enable clipboard copy/paste */
  clipboard: {
    type: [Boolean, Object] as PropType<boolean | ClipboardConfig>,
    default: undefined,
  },
  /** Enable right-click context menu */
  contextMenu: {
    type: [Boolean, Object] as PropType<boolean | ContextMenuConfig>,
    default: undefined,
  },
  /** Enable multi-column sorting */
  multiSort: {
    type: [Boolean, String, Object] as PropType<boolean | 'single' | 'multi' | MultiSortConfig>,
    default: undefined,
  },
  /** Enable column filtering */
  filtering: {
    type: [Boolean, Object] as PropType<boolean | FilterConfig<TRow>>,
    default: undefined,
  },
  /** Enable column drag-to-reorder */
  reorderColumns: {
    type: [Boolean, Object] as PropType<boolean | ReorderConfig>,
    default: undefined,
  },
  /** Enable column visibility toggle panel */
  visibility: {
    type: [Boolean, Object] as PropType<boolean | VisibilityConfig>,
    default: undefined,
  },
  /** Enable pinned/sticky columns */
  pinnedColumns: {
    type: Boolean as PropType<boolean>,
    default: undefined,
  },
  /** Enable multi-level column headers */
  groupingColumns: {
    type: [Boolean, Object] as PropType<boolean | GroupingColumnsConfig>,
    default: undefined,
  },
  /** Enable horizontal column virtualization */
  columnVirtualization: {
    type: [Boolean, Object] as PropType<boolean | ColumnVirtualizationConfig>,
    default: undefined,
  },
  /** Enable row drag-and-drop within and across grids */
  rowDragDrop: {
    type: [Boolean, Object] as PropType<boolean | RowDragDropConfig<TRow>>,
    default: undefined,
  },
  /** Enable row grouping by field values */
  groupingRows: {
    type: Object as PropType<GroupingRowsConfig>,
    default: undefined,
  },
  /** Enable pinned rows */
  pinnedRows: {
    type: [Boolean, Object] as PropType<boolean | PinnedRowsConfig>,
    default: undefined,
  },
  /** Enable rows that stick to the viewport top while their section scrolls */
  stickyRows: {
    type: Object as PropType<StickyRowsConfig>,
    default: undefined,
  },
  /** Enable hierarchical tree view */
  tree: {
    type: [Boolean, Object] as PropType<boolean | TreeConfig>,
    default: undefined,
  },
  /** Enable master-detail expandable rows */
  masterDetail: {
    type: Object as PropType<MasterDetailConfig>,
    default: undefined,
  },
  /** Enable responsive card layout */
  responsive: {
    type: [Boolean, Object] as PropType<boolean | ResponsivePluginConfig>,
    default: undefined,
  },
  /** Enable undo/redo for cell edits */
  undoRedo: {
    type: [Boolean, Object] as PropType<boolean | UndoRedoConfig>,
    default: undefined,
  },
  /** Enable CSV/JSON export */
  export: {
    type: [Boolean, Object] as PropType<boolean | ExportConfig>,
    default: undefined,
  },
  /** Enable print functionality */
  print: {
    type: [Boolean, Object] as PropType<boolean | PrintConfig>,
    default: undefined,
  },
  /** Enable pivot table functionality */
  pivot: {
    type: Object as PropType<PivotConfig>,
    default: undefined,
  },
  /** Enable server-side data operations */
  serverSide: {
    type: Object as PropType<ServerSideConfig>,
    default: undefined,
  },
  /** Enable tooltip display for header and cell content */
  tooltip: {
    type: [Boolean, Object] as PropType<boolean | TooltipConfig>,
    default: undefined,
  },
});

/**
 * Event name → CustomEvent detail type map.
 * Used internally to wire up event listeners.
 */
const EVENT_MAP = {
  'cell-click': '' as unknown as CellClickDetail,
  'row-click': '' as unknown as RowClickDetail,
  'cell-activate': '' as unknown as CellActivateDetail,
  'cell-change': '' as unknown as CellChangeDetail,
  'cell-commit': '' as unknown as CellCommitDetail,
  'cell-cancel': '' as unknown as CellCancelDetail,
  'row-commit': '' as unknown as RowCommitDetail,
  'changed-rows-reset': '' as unknown as ChangedRowsResetDetail,
  'edit-open': '' as unknown as EditOpenDetail,
  'before-edit-close': '' as unknown as BeforeEditCloseDetail,
  'edit-close': '' as unknown as EditCloseDetail,
  'dirty-change': '' as unknown as DirtyChangeDetail,
  'baselines-captured': '' as unknown as BaselinesCapturedDetail,
  'data-change': '' as unknown as DataChangeDetail,
  'sort-change': '' as unknown as SortChangeDetail,
  'filter-change': '' as unknown as FilterChangeDetail,
  'column-resize': '' as unknown as ColumnResizeDetail,
  'column-resize-reset': '' as unknown as ColumnResizeResetDetail,
  'column-move': '' as unknown as ColumnMoveDetail,
  'column-visibility': '' as unknown as ColumnVisibilityDetail,
  'column-state-change': '' as unknown as GridColumnState,
  'selection-change': '' as unknown as SelectionChangeDetail,
  'row-move': '' as unknown as RowMoveDetail,
  'row-drag-start': '' as unknown as RowDragStartDetail,
  'row-drag-end': '' as unknown as RowDragEndDetail,
  'row-drop': '' as unknown as RowDropDetail,
  'row-transfer': '' as unknown as RowTransferDetail,
  'group-toggle': '' as unknown as GroupToggleDetail,
  'group-expand': '' as unknown as GroupExpandDetail,
  'group-collapse': '' as unknown as GroupCollapseDetail,
  'tree-expand': '' as unknown as TreeExpandDetail,
  'tree-load-start': '' as unknown as TreeLoadStartDetail,
  'tree-load-end': '' as unknown as TreeLoadEndDetail,
  'tree-load-error': '' as unknown as TreeLoadErrorDetail,
  'detail-expand': '' as unknown as DetailExpandDetail,
  'responsive-change': '' as unknown as ResponsiveChangeDetail,
  'context-menu-open': '' as unknown as ContextMenuOpenDetail,
  copy: '' as unknown as CopyDetail,
  paste: '' as unknown as PasteDetail,
  'paste-rejected': '' as unknown as PasteRejectedDetail,
  // The UndoRedoPlugin emits two distinct events: `undo` and `redo` (not the
  // combined `undo-redo` name). Listening to `undo-redo` would silently never
  // fire — keep these separate to mirror the React adapter and match the
  // plugin's actual emit calls.
  undo: '' as unknown as UndoRedoDetail,
  redo: '' as unknown as UndoRedoDetail,
  'export-complete': '' as unknown as ExportCompleteDetail,
  'print-start': '' as unknown as PrintStartDetail,
  'print-complete': '' as unknown as PrintCompleteDetail,
  'tbw-scroll': '' as unknown as TbwScrollDetail,
  render: '' as unknown as RenderDetail,
} as const;

// ──────────────────────────────────────────────────────────────────────
// Forward-only event coverage guard (mirrors React `event-props.ts` and the
// Angular `Grid` directive). If a new event lands in core's `DataGridEventMap`
// — including via plugin module augmentation from `@toolbox-web/grid/all` — but
// `EVENT_MAP` has no entry, the assignment below stops type-checking.
//
// `mount-external-editor` / `mount-external-view` are internal plumbing (the
// adapter itself is the sole listener); `column-reorder-request` is an
// inter-plugin request.
//
// NOTE: the `= true` assignment is load-bearing — a bare `type` alias is never
// checked, which is how Angular's equivalent guard sat inert for releases.
// ──────────────────────────────────────────────────────────────────────
type _IntentionallyOmittedEvents = 'mount-external-editor' | 'mount-external-view' | 'column-reorder-request';

type _MissingEmits = Exclude<keyof DataGridEventMap<unknown>, keyof typeof EVENT_MAP | _IntentionallyOmittedEvents>;

const _emitsCoverCore: [_MissingEmits] extends [never]
  ? true
  : ['Missing Vue emits for core grid events:', _MissingEmits] = true;
void _emitsCoverCore;

/**
 * Emits for TbwGrid — all grid events forwarded as Vue emits.
 */
const emit = defineEmits<{
  (e: 'cell-click', event: CustomEvent<CellClickDetail<TRow>>): void;
  (e: 'row-click', event: CustomEvent<RowClickDetail<TRow>>): void;
  (e: 'cell-activate', event: CustomEvent<CellActivateDetail<TRow>>): void;
  (e: 'cell-change', event: CustomEvent<CellChangeDetail<TRow>>): void;
  (e: 'cell-commit', event: CustomEvent<CellCommitDetail<TRow>>): void;
  (e: 'cell-cancel', event: CustomEvent<CellCancelDetail>): void;
  (e: 'row-commit', event: CustomEvent<RowCommitDetail<TRow>>): void;
  (e: 'changed-rows-reset', event: CustomEvent<ChangedRowsResetDetail<TRow>>): void;
  (e: 'edit-open', event: CustomEvent<EditOpenDetail<TRow>>): void;
  (e: 'before-edit-close', event: CustomEvent<BeforeEditCloseDetail<TRow>>): void;
  (e: 'edit-close', event: CustomEvent<EditCloseDetail<TRow>>): void;
  (e: 'dirty-change', event: CustomEvent<DirtyChangeDetail<TRow>>): void;
  (e: 'baselines-captured', event: CustomEvent<BaselinesCapturedDetail>): void;
  (e: 'data-change', event: CustomEvent<DataChangeDetail>): void;
  (e: 'sort-change', event: CustomEvent<SortChangeDetail>): void;
  (e: 'filter-change', event: CustomEvent<FilterChangeDetail>): void;
  (e: 'column-resize', event: CustomEvent<ColumnResizeDetail>): void;
  (e: 'column-resize-reset', event: CustomEvent<ColumnResizeResetDetail>): void;
  (e: 'column-move', event: CustomEvent<ColumnMoveDetail>): void;
  (e: 'column-visibility', event: CustomEvent<ColumnVisibilityDetail>): void;
  (e: 'column-state-change', event: CustomEvent<GridColumnState>): void;
  (e: 'selection-change', event: CustomEvent<SelectionChangeDetail>): void;
  (e: 'row-move', event: CustomEvent<RowMoveDetail<TRow>>): void;
  (e: 'row-drag-start', event: CustomEvent<RowDragStartDetail<TRow>>): void;
  (e: 'row-drag-end', event: CustomEvent<RowDragEndDetail<TRow>>): void;
  (e: 'row-drop', event: CustomEvent<RowDropDetail<TRow>>): void;
  (e: 'row-transfer', event: CustomEvent<RowTransferDetail<TRow>>): void;
  (e: 'group-toggle', event: CustomEvent<GroupToggleDetail>): void;
  (e: 'group-expand', event: CustomEvent<GroupExpandDetail>): void;
  (e: 'group-collapse', event: CustomEvent<GroupCollapseDetail>): void;
  (e: 'tree-expand', event: CustomEvent<TreeExpandDetail<TRow>>): void;
  (e: 'tree-load-start', event: CustomEvent<TreeLoadStartDetail<TRow>>): void;
  (e: 'tree-load-end', event: CustomEvent<TreeLoadEndDetail<TRow>>): void;
  (e: 'tree-load-error', event: CustomEvent<TreeLoadErrorDetail<TRow>>): void;
  (e: 'detail-expand', event: CustomEvent<DetailExpandDetail>): void;
  (e: 'responsive-change', event: CustomEvent<ResponsiveChangeDetail>): void;
  (e: 'context-menu-open', event: CustomEvent<ContextMenuOpenDetail>): void;
  (e: 'copy', event: CustomEvent<CopyDetail>): void;
  (e: 'paste', event: CustomEvent<PasteDetail>): void;
  (e: 'paste-rejected', event: CustomEvent<PasteRejectedDetail>): void;
  (e: 'undo', event: CustomEvent<UndoRedoDetail>): void;
  (e: 'redo', event: CustomEvent<UndoRedoDetail>): void;
  (e: 'export-complete', event: CustomEvent<ExportCompleteDetail>): void;
  (e: 'print-start', event: CustomEvent<PrintStartDetail>): void;
  (e: 'print-complete', event: CustomEvent<PrintCompleteDetail>): void;
  (e: 'tbw-scroll', event: CustomEvent<TbwScrollDetail>): void;
  (e: 'render', event: CustomEvent<RenderDetail>): void;
}>();

// Template ref for the grid element
const gridRef = ref<DataGridElement<TRow> | null>(null);

// The custom-element tag this bundle's grid is registered under. Equals
// `'tbw-grid'` in the single-version case; falls back to a version-suffixed
// tag when a different grid version was registered first. See issue #339.
const gridTag = GridElement.activeTag;

// Template ref for the TeleportManager — exposes a TeleportManagerHandle
// that the teleport-bridge uses to render Vue content into grid containers
// while preserving the parent Vue context (provide/inject, Pinia, Router, i18n).
const teleportManagerRef = ref<TeleportManagerHandle | null>(null);

// Provide grid element to descendants (for useGrid composable)
provide(GRID_ELEMENT_KEY, gridRef);

// Get type defaults and icons from providers
const typeDefaults = useGridTypeDefaults();
const iconOverrides = useGridIcons();

/**
 * Create plugins from feature props. Delegates to the core registry's
 * `createPluginsFromFeatures` so dependency validation (e.g. `undoRedo`
 * requires `editing`, `clipboard` requires `selection`) and dependency
 * ordering are shared with the React adapter and `gridConfig.features`.
 *
 * The list of feature prop names is read from the `feature-prop-keys`
 * registry (pre-populated with all built-ins at module load) so third-party
 * features can extend the recognised prop set via `registerFeaturePropKey`
 * without forking `<TbwGrid>`.
 */
function createFeaturePlugins(): BaseGridPlugin[] {
  // Manual `plugins` prop is an escape hatch: when present it takes over
  // entirely, matching `<DataGrid plugins={...}>` in the React adapter.
  if (props.plugins) return [];
  const featureProps: Record<string, unknown> = {};
  for (const feature of getFeaturePropKeys()) {
    const propValue = props[feature as keyof typeof props];
    if (propValue !== undefined) {
      featureProps[feature] = propValue;
    }
  }
  return createPluginsFromFeatures(featureProps) as BaseGridPlugin[];
}

// Merged config with feature plugins
const mergedConfig = computed<GridConfig<TRow> | undefined>(() => {
  const baseConfig = props.gridConfig ?? {};
  const featurePlugins = createFeaturePlugins();
  const configPlugins = (baseConfig.plugins as BaseGridPlugin[]) ?? [];
  const manualPlugins = props.plugins ?? [];

  // Merge: manual plugins first, then feature plugins, then config plugins
  const mergedPlugins = [...manualPlugins, ...featurePlugins, ...configPlugins];

  // Apply icon overrides if provided
  const icons = iconOverrides ? { ...baseConfig.icons, ...iconOverrides } : baseConfig.icons;

  // Build core config overrides from individual props
  const coreConfigOverrides: Record<string, unknown> = {};
  if (props.sortable !== undefined) {
    coreConfigOverrides['sortable'] = props.sortable;
  }
  if (props.filterable !== undefined) {
    coreConfigOverrides['filterable'] = props.filterable;
  }
  if (props.selectable !== undefined) {
    coreConfigOverrides['selectable'] = props.selectable;
  }

  // Normalize shorthand strings to ColumnConfig objects and apply column
  // defaults. Individual column props override defaults.
  const processedColumns = props.columns
    ? applyColumnDefaults(normalizeColumns<TRow>(props.columns), props.columnDefaults)
    : undefined;

  return {
    ...baseConfig,
    ...coreConfigOverrides,
    ...(processedColumns ? { columns: processedColumns } : {}),
    ...(mergedPlugins.length > 0 ? { plugins: mergedPlugins } : {}),
    ...(icons ? { icons } : {}),
  } as GridConfig<TRow>;
});

// Unsubscribe functions for grid event listeners
const eventCleanups: (() => void)[] = [];

/**
 * Previous rows snapshot for in-place diffing (see rows watcher below).
 * Not reactive — plain variable so Vue doesn't track it.
 */
let prevRowsSnapshot: TRow[] = [];

// Setup and cleanup
onMounted(() => {
  const grid = gridRef.value;
  if (!grid) return;

  // Attach the framework adapter to the grid element
  // This enables MasterDetailPlugin and ResponsivePlugin to use Vue-based renderers
  const adapter = ensureAdapterRegistered();
  (grid as any).__frameworkAdapter = adapter;

  // Wire the TeleportManager to the bridge so non-Vue code (adapter, feature
  // bridge files) can render Vue content while preserving the parent context
  // tree. Without this, the bridge falls back to `createApp()` which mounts
  // each cell renderer / editor / detail panel / tool panel in an isolated
  // Vue tree that loses provide/inject, Pinia stores, Router, and i18n.
  if (teleportManagerRef.value) {
    setTeleportManager(grid, teleportManagerRef.value);
  }

  // Pass type defaults to the adapter
  adapter.setTypeDefaults(typeDefaults ?? null);

  // Subscribe to grid events and store unsubscribe functions
  // Subscribe to all grid events and forward as Vue emits
  for (const eventName of Object.keys(EVENT_MAP)) {
    eventCleanups.push(grid.on(eventName as string, (_d: unknown, e: CustomEvent) => emit(eventName as any, e)));
  }

  // Set initial data
  if (props.rows.length > 0) {
    grid.rows = props.rows;
    prevRowsSnapshot = props.rows;
  }
  if (mergedConfig.value) {
    // Process through adapter before passing to grid
    grid.gridConfig = mergedConfig.value;
  }
  if (props.fitMode) {
    grid.fitMode = props.fitMode;
  }
  if (props.columnInference) {
    grid.columnInference = props.columnInference;
  }
  if (props.loading !== undefined) {
    grid.loading = props.loading;
  }
  // Handle initial custom styles
  if (props.customStyles) {
    grid.ready?.().then(() => {
      if (gridRef.value) {
        gridRef.value.registerStyles?.('vue-custom-styles', props.customStyles as string);
      }
    });
  }

  // Refresh plugins after Vue children have mounted so that late-registered
  // light DOM templates (TbwGridDetailPanel, TbwGridResponsiveCard,
  // TbwGridToolPanel, TbwGridColumn slots) are picked up by the grid.
  // Mirrors the React adapter's post-mount refresh in data-grid.tsx.
  void nextTick(() => {
    const gridEl = gridRef.value;
    if (!gridEl) return;
    // Narrow with an intersection cast instead of `as unknown as` (TS
    // conventions Priority 1). `refreshColumns`/`refreshShellHeader` are
    // optional core methods that may be absent on older grid builds.
    const g = gridEl as DataGridElement<TRow> & {
      refreshColumns?: () => void;
      refreshShellHeader?: () => void;
    };
    // Feature secondary entries (e.g. `features/master-detail`,
    // `features/responsive`) install their own refresh hooks via
    // `registerPostMountRefresh`. The adapter core no longer knows which
    // plugins need refreshing or what their refresh method is called.
    notifyPostMount(gridEl);
    // Refresh columns to pick up Vue-rendered light DOM elements
    g.refreshColumns?.();
    // Refresh shell header to pick up tool panel templates
    g.refreshShellHeader?.();
  });
});

onBeforeUnmount(() => {
  const grid = gridRef.value as unknown as HTMLElement & DataGridElement<TRow>;
  if (!grid) return;

  // Unregister TeleportManager from the bridge
  setTeleportManager(grid, null);

  // Clean up custom styles
  if (props.customStyles) {
    (grid as DataGridElement).unregisterStyles?.('vue-custom-styles');
  }

  // Unsubscribe all grid event listeners
  eventCleanups.forEach((fn) => fn());
  eventCleanups.length = 0;
});

// Watch for prop changes
//
// Smart diff: when getRowId is configured and the same rows appear in the same
// order with only their values changed, route through updateRows() to patch
// in-place. This triggers only RenderPhase.VIRTUALIZATION instead of the
// heavier ROWS phase (which rebuilds the row model and re-applies sort/filter).
// Mirrors the React and Angular adapters.
//
// When Vue detects a deep mutation (newRows === oldRows — same array reference),
// the grid objects already share the reference so values are current; we still
// need to trigger a re-render via the normal rows setter.
watch(
  () => props.rows,
  (newRows, oldRows) => {
    const grid = gridRef.value;
    if (!grid) return;

    // Deep in-place mutation: Vue fired the watcher but newRows/oldRows are the
    // same reference. Grid row objects are shared, so just trigger a re-render.
    if (newRows === oldRows) {
      grid.rows = newRows;
      prevRowsSnapshot = newRows;
      return;
    }

    // New array reference: attempt value-only diff.
    const getId = mergedConfig.value?.getRowId as ((row: TRow) => string) | undefined;
    const diff = getId ? computeRowDiff(newRows, prevRowsSnapshot, getId) : null;

    if (diff !== null) {
      if (diff.length > 0) {
        // 'sync' source: authoritative host data, not a user edit — editing
        // plugin skips dirty marking / undo history for it.
        try {
          grid.updateRows(diff, 'sync');
        } catch {
          // getRowId changed alongside rows (grid still resolving ids with the
          // previous resolver) or an id vanished — fall back to a full replace.
          grid.rows = newRows;
        }
      }
      prevRowsSnapshot = newRows;
      return;
    }

    // Structural change (adds/removes/reorder), no getRowId, or initial load — full replace.
    grid.rows = newRows;
    prevRowsSnapshot = newRows;
  },
  { deep: true },
);

watch(
  mergedConfig,
  (newConfig) => {
    if (gridRef.value && newConfig) {
      gridRef.value.gridConfig = newConfig;
    }
  },
  { deep: true },
);

watch(
  () => props.fitMode,
  (newFitMode) => {
    if (gridRef.value && newFitMode) {
      gridRef.value.fitMode = newFitMode;
    }
  },
);

watch(
  () => props.columnInference,
  (newColumnInference) => {
    if (gridRef.value) {
      gridRef.value.columnInference = newColumnInference;
    }
  },
);

watch(
  () => props.loading,
  (newLoading) => {
    if (gridRef.value && newLoading !== undefined) {
      gridRef.value.loading = newLoading;
    }
  },
);

watch(
  () => props.customStyles,
  (newStyles, oldStyles) => {
    if (!gridRef.value) return;
    const grid = gridRef.value;
    if (oldStyles && !newStyles) {
      grid.unregisterStyles?.('vue-custom-styles');
    } else if (newStyles) {
      grid.ready?.().then(() => {
        if (gridRef.value) {
          gridRef.value.registerStyles?.('vue-custom-styles', newStyles);
        }
      });
    }
  },
);

// Watch for type defaults changes
watch(
  () => typeDefaults,
  (newTypeDefaults) => {
    const adapter = ensureAdapterRegistered();
    adapter.setTypeDefaults(newTypeDefaults ?? null);
  },
  { deep: true },
);

// Expose the grid element for programmatic access
defineExpose({
  /** The underlying grid element */
  gridElement: gridRef,
  /** Force a layout recalculation */
  forceLayout: () => gridRef.value?.forceLayout(),
  /** Get current grid configuration */
  getConfig: () => gridRef.value?.getConfig(),
  /** Wait for grid to be ready */
  ready: () => gridRef.value?.ready(),
});
</script>

<template>
  <!--
    TeleportManager renders portal-style Vue content into grid containers
    (cells, editors, detail panels, tool panels) while preserving the parent
    Vue context tree. Mounted as a sibling of the grid so it inherits the
    surrounding component's provide/inject, Pinia, Router, and i18n setup.
  -->
  <TeleportManager ref="teleportManagerRef" />
  <!--
    Render via `<component :is>` instead of the literal `<tbw-grid>` tag.
    When two `@toolbox-web/grid` versions coexist on a page, the second
    bundle to load registers itself under a version-suffixed tag (e.g.
    `tbw-grid-v2-11-0`); `GridElement.activeTag` reflects whichever tag
    THIS bundle actually owns. See issue #339.
  -->
  <component :is="gridTag" ref="gridRef" v-bind="$attrs">
    <slot></slot>
  </component>
</template>
